import { ReactNode } from 'react';
import { CalendarCheck, Euro, Percent, Users } from 'lucide-react';

export interface HistorySummary {
  workshopCount: number;
  bookingCount: number;
  /** Une place = un plan de travail. Un duo en occupe une seule. */
  placesCount: number;
  capacity: number;
  fillRate: number;
  revenue: number;
}

/**
 * Les réservations arrivent après les ateliers. Tant qu'elles manquent, les
 * trois tuiles qui en dépendent affichent « — » : un zéro se lirait comme
 * « aucun participant », ce qui est faux pendant le chargement comme en cas
 * d'erreur de lecture.
 */
export type ParticipantsState = 'ready' | 'loading' | 'unavailable';

interface Props {
  summary: HistorySummary;
  loading: boolean;
  participantsState: ParticipantsState;
}

const numberFormat = new Intl.NumberFormat('fr-FR');

const formatEuros = (value: number) =>
  value.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

interface TileProps {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
  /** Jauge 0-100 affichée sous la valeur ; le chiffre reste la source de vérité. */
  meter?: number;
}

const StatTile = ({ icon, label, value, hint, meter }: TileProps) => (
  <div className="flex-1 min-w-[160px] rounded-2xl border border-[#623413]/10 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
      <span className="text-[#623413]/60">{icon}</span>
      {label}
    </div>
    <p className="mt-2 text-2xl font-semibold text-[#623413]">{value}</p>
    {meter !== undefined && (
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
        <div
          className="h-full rounded-full bg-[#623413] transition-all"
          style={{ width: `${Math.min(100, Math.max(0, meter))}%` }}
        />
      </div>
    )}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const SkeletonTile = () => (
  <div className="flex-1 min-w-[160px] rounded-2xl border border-[#623413]/10 bg-white p-4 shadow-sm">
    <div className="h-3 w-24 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
    <div className="mt-3 h-6 w-16 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
  </div>
);

const HistoryStats = ({ summary, loading, participantsState }: Props) => {
  const participantsReady = participantsState === 'ready';
  const pendingHint =
    participantsState === 'loading'
      ? 'chargement…'
      : 'réservations indisponibles';

  if (loading) {
    return (
      <div className="flex flex-wrap gap-3">
        {[...Array(4)].map((_, i) => (
          <SkeletonTile key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      <StatTile
        icon={<CalendarCheck size={14} />}
        label="Ateliers"
        value={numberFormat.format(summary.workshopCount)}
        hint="sur la sélection courante"
      />
      <StatTile
        icon={<Users size={14} />}
        label="Places réservées"
        value={
          participantsReady ? numberFormat.format(summary.placesCount) : '—'
        }
        hint={
          participantsReady
            ? `${numberFormat.format(summary.bookingCount)} réservation${
                summary.bookingCount > 1 ? 's' : ''
              }`
            : pendingHint
        }
      />
      <StatTile
        icon={<Percent size={14} />}
        label="Remplissage"
        value={participantsReady ? `${summary.fillRate} %` : '—'}
        meter={participantsReady ? summary.fillRate : undefined}
        hint={
          participantsReady
            ? `${numberFormat.format(
                summary.placesCount
              )} places sur ${numberFormat.format(summary.capacity)}`
            : `capacité totale ${numberFormat.format(summary.capacity)}`
        }
      />
      <StatTile
        icon={<Euro size={14} />}
        label="Recettes estimées"
        value={participantsReady ? formatEuros(summary.revenue) : '—'}
        hint={
          participantsReady
            ? "d'après les tarifs de chaque atelier"
            : pendingHint
        }
      />
    </div>
  );
};

export default HistoryStats;
